import defaultSound from "../assets/sounds/online-order-ring.wav";
import File from "../services/File";

/**
 * Звук уведомления о новом онлайн-заказе: встроенный короткий звонок или
 * файл, загруженный в настройках заказа (хранится в приватном бакете,
 * читается через file.download и кэшируется как object URL по id файла).
 */

export interface OnlineOrderSoundRef {
	fileId: number;
	name: string;
}

export const ONLINE_ORDER_SOUND_MAX_SECONDS = 15;
export const ONLINE_ORDER_SOUND_FORMATS = ["mp3", "wav", "ogg", "m4a", "aac"];
export const ONLINE_ORDER_SOUND_ACCEPT = ONLINE_ORDER_SOUND_FORMATS.map(
	(ext) => `.${ext}`,
).join(", ");

const urlCache = new Map<number, Promise<string>>();
let current: HTMLAudioElement | null = null;
let stopTimer: ReturnType<typeof setTimeout> | null = null;

export function getDefaultOnlineOrderSoundUrl() {
	return defaultSound;
}

export function resolveOnlineOrderSoundUrl(
	sound: OnlineOrderSoundRef | null | undefined,
): Promise<string> {
	const fileId = sound?.fileId;
	if (!fileId) return Promise.resolve(defaultSound);

	let pending = urlCache.get(fileId);
	if (!pending) {
		pending = File.download(fileId)
			.then((blob) => (blob ? URL.createObjectURL(blob) : defaultSound))
			.catch(() => defaultSound);
		urlCache.set(fileId, pending);
	}

	return pending;
}

export function invalidateOnlineOrderSound(fileId: number) {
	const pending = urlCache.get(fileId);
	urlCache.delete(fileId);
	pending?.then((url) => {
		if (url !== defaultSound) URL.revokeObjectURL(url);
	});
}

export function stopOnlineOrderSound() {
	if (stopTimer) clearTimeout(stopTimer);
	stopTimer = null;
	if (!current) return;
	current.pause();
	current.src = "";
	current = null;
}

export async function playOnlineOrderSound(
	sound: OnlineOrderSoundRef | null | undefined,
) {
	const url = await resolveOnlineOrderSoundUrl(sound);

	// Новый заказ во время звонка — перезапускаем звук, а не накладываем.
	stopOnlineOrderSound();

	const audio = new Audio(url);
	audio.volume = 1;
	current = audio;

	// Страховка: файл не длиннее лимита, но мало ли.
	stopTimer = setTimeout(() => {
		if (current === audio) stopOnlineOrderSound();
	}, (ONLINE_ORDER_SOUND_MAX_SECONDS + 1) * 1000);

	try {
		await audio.play();
	} catch (error) {
		// Автоплей заблокирован до первого жеста пользователя — не падаем.
		// eslint-disable-next-line no-console
		console.warn("[onlineOrderSound] play failed", error);
		if (current === audio) stopOnlineOrderSound();
	}
}
